import type { BeatSlot } from '../types';
import {
  createEmptyRhythmPattern,
  type RhythmCell,
  type RhythmPattern,
} from './rhythmPattern';

export type BeatFeedbackStatus = 'correct' | 'wrong';

export interface BeatCompareResult {
  measureIndex: number;
  beatIndex: number;
  status: BeatFeedbackStatus;
}

export interface RhythmCompareResult {
  score: number;
  total: number;
  allCorrect: boolean;
  beats: BeatCompareResult[];
}

function cellsMatch(a: RhythmCell[], b: RhythmCell[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (cell, i) => cell.hit === b[i].hit && cell.figure === b[i].figure,
  );
}

function slotsMatch(answer: BeatSlot & { cells: RhythmCell[] }, guess: BeatSlot & { cells: RhythmCell[] }): boolean {
  if (!guess.active) return false;
  if (answer.figure !== guess.figure) return false;
  if (answer.span !== guess.span) return false;
  return cellsMatch(answer.cells, guess.cells);
}

export function compareRhythmPatterns(
  answer: RhythmPattern,
  guess: RhythmPattern,
): RhythmCompareResult {
  const beats: BeatCompareResult[] = [];
  let score = 0;
  let total = 0;

  for (let mi = 0; mi < answer.measures.length; mi++) {
    const am = answer.measures[mi];
    const gm = guess.measures[mi];
    if (!gm) continue;

    for (let bi = 0; bi < am.slots.length; bi++) {
      const aSlot = am.slots[bi];
      if (!aSlot.active) continue;
      total++;
      const gSlot = gm.slots[bi];
      const correct = slotsMatch(
        aSlot as BeatSlot & { cells: RhythmCell[] },
        gSlot as BeatSlot & { cells: RhythmCell[] },
      );
      if (correct) score++;
      beats.push({
        measureIndex: mi,
        beatIndex: bi,
        status: correct ? 'correct' : 'wrong',
      });
    }
  }

  return {
    score,
    total,
    allCorrect: total > 0 && score === total,
    beats,
  };
}

export function beatFeedbackMap(
  result: RhythmCompareResult,
): Record<string, BeatFeedbackStatus> {
  const map: Record<string, BeatFeedbackStatus> = {};
  for (const b of result.beats) {
    map[`${b.measureIndex}-${b.beatIndex}`] = b.status;
  }
  return map;
}

export function cloneRhythmPattern(pattern: RhythmPattern): RhythmPattern {
  return JSON.parse(JSON.stringify(pattern)) as RhythmPattern;
}

export function emptyGuessFromAnswer(answer: RhythmPattern): RhythmPattern {
  return createEmptyRhythmPattern(
    answer.measures.length,
    answer.beatsPerMeasure,
  );
}
